import { useMemo, useState } from 'react';
import { adminExpenseApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Search, Download, ImageIcon, FileText, RotateCcw, Loader2, ExternalLink } from 'lucide-react';

// Admin-only "Receipt Recovery" tool. An employee can lose the typed-in
// details of a draft expense report (browser closed mid-edit, session
// timeout, etc.) while the receipt IMAGES they already uploaded are
// still sitting safely in blob storage — they're just invisible in the
// normal admin views because they have no matching line item. This page
// lets an admin look up any employee by email/userId and see every
// receipt across every one of their reports (flagging the orphaned
// ones), plus any stray blobs whose metadata row is gone entirely.

function fileIsImage(receipt) {
  const m = (receipt.mimeType || '').toLowerCase();
  if (m.startsWith('image/')) return true;
  const ext = (receipt.fileName || '').split('.').pop()?.toLowerCase();
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic', 'heif'].includes(ext);
}

function ReceiptRow({ receipt, selectable, selected, onToggle }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-background/40 px-3 py-2.5">
      {selectable && (
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0 cursor-pointer accent-primary"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${receipt.fileName}`}
        />
      )}
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {fileIsImage(receipt) ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{receipt.fileName || receipt.rowKey}</span>
          {receipt.orphaned && <Badge variant="warning">orphaned</Badge>}
        </div>
        <div className="text-xs text-muted-foreground">
          {receipt.uploadedAt ? new Date(receipt.uploadedAt).toLocaleString() : 'Unknown upload time'}
        </div>
      </div>
      {receipt.url ? (
        <a
          href={receipt.url}
          target="_blank"
          rel="noopener noreferrer"
          download={receipt.fileName || ''}
          className="inline-flex items-center gap-1.5 shrink-0 rounded-md px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/10"
        >
          <Download className="h-3.5 w-3.5" /> Download
        </a>
      ) : (
        <span className="shrink-0 text-xs text-muted-foreground">No URL</span>
      )}
    </div>
  );
}

export default function ReceiptRecoveryPage() {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set()); // keys: `${reportId}::${receiptRowKey}`
  const [restoring, setRestoring] = useState(false);

  const selectedCount = selected.size;

  // Which source report the selected receipts belong to — restore
  // requires all selections to come from a single report (they get
  // moved into one new draft together).
  const selectedReportId = useMemo(() => {
    const ids = new Set(Array.from(selected).map((k) => k.split('::')[0]));
    return ids.size === 1 ? Array.from(ids)[0] : null;
  }, [selected]);

  async function handleSearch(e) {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) {
      toast.error('Enter an employee email or user ID');
      return;
    }
    setLoading(true);
    setData(null);
    setSelected(new Set());
    try {
      const res = await adminExpenseApi.recoverReceipts(trimmed);
      setData(res);
      const totalReceipts = (res.reports || []).reduce((n, r) => n + r.receipts.length, 0);
      const orphanCount = (res.reports || []).reduce(
        (n, r) => n + r.receipts.filter((rec) => rec.orphaned).length, 0
      );
      const looseCount = (res.looseBlobs || []).length;
      toast.success(
        `Found ${res.reports.length} report${res.reports.length === 1 ? '' : 's'}, ${totalReceipts} receipt${totalReceipts === 1 ? '' : 's'}` +
        (orphanCount ? ` (${orphanCount} orphaned)` : '') +
        (looseCount ? `, ${looseCount} stray blob${looseCount === 1 ? '' : 's'}` : '')
      );
    } catch (err) {
      toast.error(`Lookup failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  function toggleReceipt(reportId, rowKey) {
    const key = `${reportId}::${rowKey}`;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Restore only works within one source report — swap the
        // selection set to the new report when the admin picks a
        // receipt from a different one.
        const currentReportIds = new Set(Array.from(prev).map((k) => k.split('::')[0]));
        if (currentReportIds.size > 0 && !currentReportIds.has(reportId)) {
          next.clear();
        }
        next.add(key);
      }
      return next;
    });
  }

  async function handleRestore() {
    if (!selectedReportId || selectedCount === 0 || !data?.user) return;
    const receiptRowKeys = Array.from(selected)
      .filter((k) => k.startsWith(`${selectedReportId}::`))
      .map((k) => k.split('::')[1]);

    setRestoring(true);
    const tid = toast.loading(`Restoring ${receiptRowKeys.length} receipt${receiptRowKeys.length === 1 ? '' : 's'} into a new draft…`);
    try {
      const res = await adminExpenseApi.restoreReceipts({
        userId: data.user.rowKey,
        receiptRowKeys,
        fromReportId: selectedReportId,
      });
      toast.success(
        `Restored ${res.moved} receipt${res.moved === 1 ? '' : 's'} into draft ${res.claimRef}` +
        (res.errors?.length ? ` (${res.errors.length} failed)` : ''),
        { id: tid }
      );
      setSelected(new Set());
      // Refresh the lookup so the moved receipts show up under the new draft.
      const refreshed = await adminExpenseApi.recoverReceipts(data.user.email || data.user.rowKey);
      setData(refreshed);
    } catch (err) {
      toast.error(`Restore failed: ${err.message}`, { id: tid });
    } finally {
      setRestoring(false);
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Receipt Recovery</h1>
        <p className="text-muted-foreground">
          Look up an employee to recover expense receipts that disappeared from drafts or lost their line item.
        </p>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex items-end gap-3">
            <div className="flex-1">
              <Input
                label="Employee email or user ID"
                placeholder="jane.doe@profun.com"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Button type="submit" loading={loading}>
              <Search className="h-4 w-4" /> Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center justify-center rounded-xl border border-border bg-card p-16 shadow-sm">
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-sm">Searching…</span>
          </div>
        </div>
      )}

      {!loading && data && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{data.user.displayName}</CardTitle>
              <CardDescription>{data.user.email} &middot; {data.reports.length} report{data.reports.length === 1 ? '' : 's'}</CardDescription>
            </CardHeader>
          </Card>

          {selectedCount > 0 && (
            <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
              <span className="text-sm font-medium">
                {selectedCount} receipt{selectedCount === 1 ? '' : 's'} selected
                {!selectedReportId && ' — mixed source reports, restore disabled'}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRestore}
                loading={restoring}
                disabled={!selectedReportId}
              >
                <RotateCcw className="h-3.5 w-3.5" /> Restore selected into a new draft
              </Button>
            </div>
          )}

          {data.reports.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card/50 p-16 text-center">
              <p className="text-sm text-muted-foreground">This employee has no expense reports.</p>
            </div>
          )}

          {data.reports.map((report) => (
            <Card key={report.rowKey}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="text-base">{report.claimRef || report.rowKey}</CardTitle>
                  <CardDescription>
                    {report.claimPurpose || 'No purpose set'} &middot; {report.lineItemCount} line item{report.lineItemCount === 1 ? '' : 's'}
                    {report.createdAt ? ` · created ${new Date(report.createdAt).toLocaleDateString()}` : ''}
                  </CardDescription>
                </div>
                <Badge variant={report.status === 'approved' ? 'success' : report.status === 'rejected' ? 'danger' : 'default'}>
                  {report.status}
                </Badge>
              </CardHeader>
              <CardContent>
                {report.receipts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No receipts on this report.</p>
                ) : (
                  <div className="space-y-2">
                    {report.receipts.map((receipt) => {
                      const key = `${report.rowKey}::${receipt.rowKey}`;
                      return (
                        <ReceiptRow
                          key={key}
                          receipt={receipt}
                          selectable
                          selected={selected.has(key)}
                          onToggle={() => toggleReceipt(report.rowKey, receipt.rowKey)}
                        />
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {data.looseBlobs && data.looseBlobs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Stray blobs (no metadata row)</CardTitle>
                <CardDescription>
                  Files found directly in storage with no matching ExpenseReceipts row — usually left behind by a
                  deleted report or receipt. These can only be downloaded, not restored automatically.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.looseBlobs.map((blob) => (
                  <div key={blob.blobPath} className="flex items-center gap-3 rounded-lg border border-border bg-background/40 px-3 py-2.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                      {fileIsImage(blob) ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{blob.fileName}</div>
                      <div className="text-xs text-muted-foreground">
                        From report {blob.reportId || 'unknown'}
                        {blob.uploadedAt ? ` · ${new Date(blob.uploadedAt).toLocaleString()}` : ''}
                      </div>
                    </div>
                    {blob.url ? (
                      <a
                        href={blob.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={blob.fileName || ''}
                        className="inline-flex items-center gap-1.5 shrink-0 rounded-md px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/10"
                      >
                        <Download className="h-3.5 w-3.5" /> Download
                      </a>
                    ) : (
                      <span className="shrink-0 text-xs text-muted-foreground">No URL</span>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {data.notes && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-warning">
              <ExternalLink className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{data.notes}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
